import type { ObjectPhoto } from '@gmn-portal/backend';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';

import { payloadClient } from '.';

export const objectPhotoKeys = {
  all: ['object-photos'] as const,
  list: (objectId: string, fieldKey: string) =>
    [...objectPhotoKeys.all, 'list', objectId, fieldKey] as const,
} as const;

export const useObjectPhotos = (
  objectId: string | null | undefined,
  fieldKey: string,
  params?: { disabled?: boolean }
) => {
  return useQuery({
    queryKey: objectPhotoKeys.list(objectId ?? '', fieldKey),
    enabled: !params?.disabled && !!objectId,
    // Fail fast: the default 3 retries would show the loading skeleton for ~7s on errors.
    retry: 1,
    queryFn: async () =>
      payloadClient.find({
        collection: 'object-photos',
        where: {
          and: [{ object: { equals: objectId } }, { fieldKey: { equals: fieldKey } }],
        },
        sort: 'createdAt',
        limit: 200,
        depth: 0,
      }),
  });
};

/** A photo picked in edit mode that is uploaded when the object is saved. */
export type PendingObjectPhoto = { id: string; file: File };

/**
 * Form value of a `photos` metadata field. Photos themselves live in the `object-photos`
 * collection; the form only stages changes until the object is saved.
 */
export type ObjectPhotosFieldValue = {
  added?: PendingObjectPhoto[];
  removed?: string[];
};

export type ObjectPhotoChange = { fieldKey: string; value: ObjectPhotosFieldValue };

const isObjectPhotosFieldValue = (value: unknown): value is ObjectPhotosFieldValue =>
  !!value && typeof value === 'object' && !Array.isArray(value);

/** Collects staged photo changes for all `photos` fields of a metadata schema. */
export const collectObjectPhotoChanges = (
  values: Record<string, unknown> | undefined,
  schema: { properties?: Record<string, { type?: string }> } | null | undefined
): ObjectPhotoChange[] =>
  Object.entries(schema?.properties ?? {})
    .filter(([, field]) => field?.type === 'photos')
    .map(([fieldKey]) => ({ fieldKey, value: values?.[fieldKey] }))
    .filter(
      (change): change is ObjectPhotoChange =>
        isObjectPhotosFieldValue(change.value) &&
        ((change.value.added?.length ?? 0) > 0 || (change.value.removed?.length ?? 0) > 0)
    );

/**
 * Applies staged photo changes after the object was saved: deletions first, then uploads.
 * Each change is attempted independently; returns the number of failed operations.
 */
export const useApplyObjectPhotoChanges = () => {
  const queryClient = useQueryClient();

  return useCallback(
    async (objectId: string, changes: ObjectPhotoChange[]) => {
      let failed = 0;

      for (const { fieldKey, value } of changes) {
        for (const id of value.removed ?? []) {
          try {
            await payloadClient.deleteById({ collection: 'object-photos', id });
          } catch {
            failed += 1;
          }
        }
        for (const { file } of value.added ?? []) {
          try {
            await payloadClient.create({
              collection: 'object-photos',
              data: { object: objectId, fieldKey },
              file,
            });
          } catch {
            failed += 1;
          }
        }
      }

      await queryClient.invalidateQueries({ queryKey: objectPhotoKeys.all });
      return { failed };
    },
    [queryClient]
  );
};

export type ObjectPhotoSize = 'thumbnail' | 'full';

/** Converts a Payload file URL (relative or absolute) into a path below the API root. */
const toApiPath = (url: string) => {
  const apiPath = new URL(payloadClient.apiURL, window.location.origin).pathname.replace(/\/$/, '');
  const pathname = new URL(url, window.location.origin).pathname;
  return pathname.startsWith(apiPath) ? pathname.slice(apiPath.length) : pathname;
};

const getObjectPhotoUrl = (photo: ObjectPhoto, size: ObjectPhotoSize) =>
  (size === 'thumbnail' ? photo.sizes?.thumbnail?.url : null) || photo.url || null;

/**
 * Loads a photo with the JWT (the API client omits cookies, so a plain `<img src>` would
 * be unauthenticated) and exposes it as an object URL that is revoked on change/unmount.
 */
export const useObjectPhotoBlobUrl = (photo: ObjectPhoto | null, size: ObjectPhotoSize) => {
  const url = photo ? getObjectPhotoUrl(photo, size) : null;
  const [state, setState] = useState<{ src: string | null; isError: boolean }>({
    src: null,
    isError: false,
  });

  useEffect(() => {
    if (!url) {
      setState({ src: null, isError: false });
      return;
    }

    let objectUrl: string | null = null;
    let cancelled = false;
    setState({ src: null, isError: false });

    const load = async () => {
      try {
        // Photo URLs are immutable (random filenames), so let the browser cache them.
        const request = await payloadClient.createRequest(toApiPath(url), {
          method: 'GET',
          cache: 'default',
        });
        const response = await payloadClient.fetcher(request);
        if (!response.ok) {
          throw new Error(`Failed to load photo (${response.status})`);
        }
        const blob = await response.blob();
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setState({ src: objectUrl, isError: false });
      } catch {
        if (!cancelled) {
          setState({ src: null, isError: true });
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [url]);

  return state;
};
